/**
 * Validacao minima, sem dependencias externas.
 *
 * Cada modelo de dados tem de ter validacao (regra de codificacao 4). Zod faria
 * o mesmo, mas acrescenta ~13 kB ao bundle inicial de um aplicativo que tem de
 * abrir em menos de 2 segundos num Android modesto — por isso o mantemos aqui.
 */

export type ValidationIssue = {
  /** Caminho do campo, ex. 'items[0].amount'. */
  path: string;
  /** Mensagem em portugues, pronta a mostrar ao usuário. */
  message: string;
};

export type ValidationResult<T> =
  | { ok: true; value: T }
  | { ok: false; issues: ValidationIssue[] };

export type Validator<T> = (value: unknown, path?: string) => ValidationResult<T>;

const ok = <T>(value: T): ValidationResult<T> => ({ ok: true, value });
const fail = (path: string, message: string): ValidationResult<never> => ({
  ok: false,
  issues: [{ path, message }],
});

export const v = {
  string(opts: { min?: number; max?: number; label?: string } = {}): Validator<string> {
    return (value, path = '') => {
      const name = (opts.label ?? path) || 'campo';
      if (typeof value !== 'string') return fail(path, `${name} tem de ser texto`);
      const trimmed = value.trim();
      if (opts.min != null && trimmed.length < opts.min) {
        return fail(
          path,
          opts.min === 1
            ? `${name} e obrigatorio`
            : `${name} tem de ter pelo menos ${opts.min} caracteres`,
        );
      }
      if (opts.max != null && trimmed.length > opts.max) {
        return fail(path, `${name} nao pode ter mais de ${opts.max} caracteres`);
      }
      return ok(trimmed);
    };
  },

  number(opts: { min?: number; max?: number; integer?: boolean; label?: string } = {}): Validator<number> {
    return (value, path = '') => {
      const name = (opts.label ?? path) || 'valor';
      const num = typeof value === 'string' ? Number(value) : value;
      if (typeof num !== 'number' || !Number.isFinite(num)) {
        return fail(path, `${name} tem de ser um numero`);
      }
      if (opts.integer && !Number.isInteger(num)) {
        return fail(path, `${name} tem de ser um numero inteiro`);
      }
      if (opts.min != null && num < opts.min) {
        return fail(path, `${name} nao pode ser menor que ${opts.min}`);
      }
      if (opts.max != null && num > opts.max) {
        return fail(path, `${name} nao pode ser maior que ${opts.max}`);
      }
      return ok(num);
    };
  },

  boolean(label?: string): Validator<boolean> {
    return (value, path = '') => {
      if (typeof value !== 'boolean') {
        return fail(path, `${(label ?? path) || 'campo'} tem de ser verdadeiro ou falso`);
      }
      return ok(value);
    };
  },

  /** Data ISO-8601. Aceita Date e converte. */
  isoDate(label?: string): Validator<string> {
    return (value, path = '') => {
      const name = (label ?? path) || 'data';
      if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) return fail(path, `${name} invalida`);
        return ok(value.toISOString());
      }
      if (typeof value !== 'string') return fail(path, `${name} tem de ser uma data`);
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return fail(path, `${name} invalida`);
      return ok(parsed.toISOString());
    };
  },

  oneOf<const T extends readonly string[]>(
    values: T,
    label?: string,
  ): Validator<T[number]> {
    return (value, path = '') => {
      if (typeof value !== 'string' || !values.includes(value)) {
        return fail(
          path,
          `${(label ?? path) || 'campo'} tem de ser um de: ${values.join(', ')}`,
        );
      }
      return ok(value as T[number]);
    };
  },

  optional<T>(inner: Validator<T>): Validator<T | undefined> {
    return (value, path = '') => {
      if (value === undefined || value === null || value === '') return ok(undefined);
      return inner(value, path);
    };
  },

  array<T>(inner: Validator<T>, opts: { min?: number; label?: string } = {}): Validator<T[]> {
    return (value, path = '') => {
      const name = (opts.label ?? path) || 'lista';
      if (!Array.isArray(value)) return fail(path, `${name} tem de ser uma lista`);
      if (opts.min != null && value.length < opts.min) {
        return fail(path, `${name} precisa de pelo menos ${opts.min} item(s)`);
      }
      const issues: ValidationIssue[] = [];
      const out: T[] = [];
      value.forEach((item, i) => {
        const result = inner(item, `${path}[${i}]`);
        if (result.ok) out.push(result.value);
        else issues.push(...result.issues);
      });
      return issues.length ? { ok: false, issues } : ok(out);
    };
  },

  object<S extends Record<string, Validator<unknown>>>(
    shape: S,
  ): Validator<{ [K in keyof S]: S[K] extends Validator<infer U> ? U : never }> {
    return (value, path = '') => {
      if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return fail(path, 'Dados invalidos');
      }
      const input = value as Record<string, unknown>;
      const issues: ValidationIssue[] = [];
      const out: Record<string, unknown> = {};
      for (const [key, validator] of Object.entries(shape)) {
        const childPath = path ? `${path}.${key}` : key;
        const result = validator(input[key], childPath);
        if (result.ok) {
          if (result.value !== undefined) out[key] = result.value;
        } else {
          issues.push(...result.issues);
        }
      }
      return issues.length
        ? { ok: false, issues }
        : ok(out as never);
    };
  },
};

/** Junta as mensagens numa frase unica para leitores de tela e para voz. */
export function issuesToText(issues: ValidationIssue[]): string {
  if (issues.length === 0) return '';
  if (issues.length === 1) return issues[0]!.message;
  return issues.map((i) => `• ${i.message}`).join('\n');
}
